/**
 * Sprint 23.8 — ActionButtonRow (shared action strip).
 *
 * A row of inline buttons + a trailing overflow dots menu. Extracted from the
 * Sprint 23.6 v2 AgentActionRow's button strip so the agent profile and the
 * workspace home identity strip share one component (buttons + ⋮ menu) instead
 * of cloning the button CSS. Each button is a `{label, icon, onClick}`; the
 * dots menu reuses the existing `<ActionMenu>`.
 *
 * `dense` switches to the workspace-home sizing (11px / 5×10 padding / 12px
 * icon); the default is the agent-profile sizing (12px / 6×12 / 14px icon).
 */

import { type CSSProperties } from "react";
import { ActionMenu, type ActionMenuItem } from "./ActionMenu";

export interface ActionButtonRowAction {
  label: string;
  /** Tabler icon name without the "ti-" prefix. */
  icon: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}

export interface ActionButtonRowProps {
  actions: ActionButtonRowAction[];
  menuItems?: ActionMenuItem[];
  /** Tighter sizing for the workspace home identity strip. */
  dense?: boolean;
}

export function ActionButtonRow({ actions, menuItems, dense }: ActionButtonRowProps) {
  const btn = dense ? denseBtn : primaryBtn;
  const iconSize = dense ? 12 : 14;
  return (
    <div style={row}>
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          onClick={a.onClick}
          disabled={a.disabled}
          title={a.title}
          style={a.disabled ? { ...btn, opacity: 0.55, cursor: "not-allowed" } : btn}
        >
          <i className={`ti ti-${a.icon}`} style={{ fontSize: iconSize }} aria-hidden="true" />
          {a.label}
        </button>
      ))}
      {menuItems && menuItems.length > 0 && <ActionMenu items={menuItems} />}
    </div>
  );
}

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const primaryBtn: CSSProperties = {
  fontSize: 12,
  padding: "6px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const denseBtn: CSSProperties = {
  ...primaryBtn,
  fontSize: 11,
  padding: "5px 10px",
  gap: 4,
};
